import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Tooltip,
  TextField,
  Autocomplete,
  Popper,
  Paper,
} from '@mui/material';
import { toTitleCase } from '../../utils/casing';
import { formatSearchDescription } from '../../utils/carBrand';

const ScrollFadePaper = React.forwardRef(function ScrollFadePaper(
  { sx, children, ...rest },
  forwardedRef
) {
  const [canScrollDown, setCanScrollDown] = useState(false);
  const paperRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      paperRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );

  useEffect(() => {
    const paper = paperRef.current;
    if (!paper) return;

    const scrollable =
      paper.querySelector('ul') ||
      paper.querySelector('[role="listbox"]') ||
      paper;

    const check = () => {
      setCanScrollDown(
        scrollable.scrollHeight -
          scrollable.scrollTop -
          scrollable.clientHeight >
          1
      );
    };

    // Initial check + re-check when children change
    check();
    // Use a short delay to let the menu render its items
    const t = setTimeout(check, 50);

    scrollable.addEventListener('scroll', check, { passive: true });
    return () => {
      scrollable.removeEventListener('scroll', check);
      clearTimeout(t);
    };
  }, [children]);

  return (
    <Paper ref={setRefs} sx={{ ...sx, position: 'relative' }} {...rest}>
      {children}
      {canScrollDown && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60px',
            background:
              'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
            pointerEvents: 'none',
            borderRadius: '0 0 12px 12px',
          }}
        />
      )}
    </Paper>
  );
});

const sizeOverrides = {
  'extra-small': '219px',
  small: '301px',
  medium: '403px',
  large: '534px',
  half: '500px',
  full: '1000px',
  'vehicle-return': '324px',
  'vehicle-return-full': '662px',
  'vehicle-full': '448px',
  'accounts-half': '416px',
  'accounts-full': '864px',
};

const yearsToLabel = (v) => {
  if (v === '' || v == null) return 'Never';
  const n = Number(v);
  if (!Number.isFinite(n)) {
    // already a label like "7+" / "Never" / "3"
    const s = String(v).trim();
    if (s === '0') return 'Never';
    return s;
  }
  if (n <= 0) return 'Never';
  if (n >= 7) return '7+';
  return String(n); // "1".."6"
};

function getFromPath(obj, path) {
  if (!obj || !path) return undefined;
  return path
    .split('.')
    .reduce(
      (acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined),
      obj
    );
}
function uniqueStrings(arr) {
  const seen = new Set();
  const out = [];
  for (const s of arr) {
    const k = String(s || '').trim();
    if (!k) continue;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

// Clears a list of dot-paths on the form values.
// Defaults to writing '' (empty string), but you can pass a custom value.
const clearPaths = (paths = [], writeFn, clearTo = '') => {
  if (!Array.isArray(paths) || !paths.length || typeof writeFn !== 'function')
    return;
  for (const p of paths) {
    // skip falsy / non-string paths safely
    if (!p || typeof p !== 'string') continue;
    writeFn(p, clearTo);
  }
};

const DropdownComponent = ({
  name,
  question,
  value,
  values,
  onChange,
  onNext,
  onExtraOptionAdvance,
  setValidationState,
  registerValidation,
  inputRef,
  nextFieldRef,
  isMobile,
  layoutVariant = 'default',
  readOnly = false,
}) => {
  const isAccountLayout = layoutVariant === 'account';
  const [error, setError] = useState('');
  const fieldRef = inputRef || useRef(null);

  const fieldWidth = question.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;
  const menuWidth = isAccountLayout
    ? fieldWidth
    : isMobile
      ? '80vw'
      : fieldWidth;
  const fieldHeight = isMobile ? '51px' : '53px';
  const fontSize = '1rem';
  const labelId = `${name}-label`;

  // --- Search / numeric mode flags ---
  const isSearchEnabled = useMemo(() => {
    const flag =
      question.enableVehicleSearch ??
      question.enableTextSearch ??
      question.enableSearch;
    return String(flag).toLowerCase() === 'true';
  }, [question]);

  const numericMode = useMemo(() => {
    const v = question.enableVehicleSearch;
    return String(v).toLowerCase() === 'true';
  }, [question]);

  const coerceVal = (v, t) => {
    if (!t) return v;
    if (t === 'string') return v == null ? '' : String(v);
    if (t === 'number') return v == null || v === '' ? null : Number(v);
    if (t === 'upper') return v == null ? '' : String(v).toUpperCase();
    if (t === 'lower') return v == null ? '' : String(v).toLowerCase();
    if (t === 'bucket7Plus') {
      const n = Number(v);
      if (Number.isFinite(n)) return n >= 7 ? '7+' : String(n);
      // fallback if not a number
      return v == null ? '' : String(v);
    }
    return v;
  };

  // Write to dot-paths in formValues via onChange
  const writeToFormValues = (path, value) => {
    if (!path) return;
    const parts = String(path).split('.');
    const root = parts.shift();
    if (!root) return;

    if (parts.length === 0) {
      onChange(root, value);
      return;
    }
    const existing = (values && values[root]) || {};
    // immutable shallow clone chain
    let cursor = Array.isArray(existing) ? [...existing] : { ...existing };
    let obj = cursor;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      const next = obj[key];
      obj[key] = Array.isArray(next)
        ? [...next]
        : typeof next === 'object' && next !== null
          ? { ...next }
          : {};
      obj = obj[key];
    }
    obj[parts[parts.length - 1]] = value;
    onChange(root, cursor);
  };

  // --- helpers (keep these near the top of the file) ---
  const eqi = (a, b) =>
    String(a || '')
      .trim()
      .toLowerCase() ===
    String(b || '')
      .trim()
      .toLowerCase();

  const pick = (obj, keys) => {
    for (const k of keys) {
      const v = getFromPath(obj, k);
      if (v !== undefined && v !== null && String(v).trim?.() !== '') return v;
    }
    return '';
  };

  // Parses: 1990-06-29 / 1990-06-29T00:00:00 / 1990/06/29 / 29-06-1990
  const parseDateFlexible = (raw) => {
    if (!raw) return null;
    const s = String(raw);
    let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) {
      const [, y, mo, d] = m;
      return { year: y, month: mo.padStart(2, '0'), day: d.padStart(2, '0') };
    }
    m = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
    if (m) {
      const [, y, mo, d] = m;
      return { year: y, month: mo.padStart(2, '0'), day: d.padStart(2, '0') };
    }
    m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (m) {
      const [, d, mo, y] = m;
      return { year: y, month: mo.padStart(2, '0'), day: d.padStart(2, '0') };
    }
    return null;
  };

  // Parses: "29 Jun 1990" (your form shows this format too)
  const parseDateDMonY = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    const m = s.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
    if (!m) return null;
    const [, dStr, monStr, y] = m;
    const map = {
      jan: '01',
      feb: '02',
      mar: '03',
      apr: '04',
      may: '05',
      jun: '06',
      jul: '07',
      aug: '08',
      sep: '09',
      oct: '10',
      nov: '11',
      dec: '12',
    };
    const mo = map[monStr.toLowerCase()];
    if (!mo) return null;
    return { year: y, month: mo, day: String(dStr).padStart(2, '0') };
  };

  const toNum = (v) => {
    if (v === '' || v == null) return '';
    const s = String(v).trim();
    if (s.endsWith('+')) {
      const base = Number(s.slice(0, -1));
      return Number.isFinite(base) ? base : '';
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : '';
  };

  // Build mainDriverDetails **only** from a driver item
  const buildFromDriverItem = (item, fallbackFullName) => {
    const fullName = item?.FullName || fallbackFullName || '';
    const firstName = item?.Name || '';
    const lastName = item?.Surname || '';

    // DOB
    let bd = parseDateFlexible(item?.DateOfBirth);
    if (!bd && item?.DateOfBirth) {
      // if API sends other weird string, try D Mon YYYY as last resort
      bd = parseDateDMonY(item?.DateOfBirth);
    }
    const by = bd?.year || '';
    const bm = bd?.month || '';
    const bday = bd?.day || '';
    const birthDateString = by && bm && bday ? `${by}-${bm}-${bday}` : '';

    return {
      fullName: String(fullName),
      lastName: String(lastName),
      firstName: String(firstName),
      birthDate: { year: String(by), month: String(bm), day: String(bday) },
      birthDateString,
      gender: item?.Gender, // (not present on driver item)
      maritalStatus: item?.MaritalStatus, // (not present on driver item)
      licenseFirstIssueDate: {
        year: item?.DateLicenseObtainedParts?.Year,
        month: item?.DateLicenseObtainedParts?.Month,
        day: '',
      }, // (not present)
      licenseType: item?.LicenseTypeName, // (not present)
      uninterruptedCarInsurance: yearsToLabel(item?.ComprehensiveInsDuration),
      lastClaimYears: yearsToLabel(item?.LastClaimDuration),
    };
  };

  const buildCarDetailsFromVehicle = (item, selectedLabel) => ({
    VehicleYear: item?.VehicleYear ?? '',
    SearchDescription: formatSearchDescription(
      String(selectedLabel ?? item?.VehicleDescription ?? '')
    ),
    SearchDescriptionRaw: String(
      selectedLabel ?? item?.VehicleDescription ?? ''
    ),
    VehicleKey: item?.VehicleKey ?? '',
    VehicleMake: item?.VehicleMake ?? '',
    VehicleModel: item?.VehicleModel ?? '',
    VehicleUsePk: item?.VehicleUsePk ?? '',
  });

  // Supplement any blank fields from formValues (when selected == values.fullName)
  const supplementFromFormValues = (details, values, sameDriverItem) => {
    // names
    if (!details.firstName)
      details.firstName = String(
        pick(values, ['FirstName', 'firstName']) || ''
      );
    if (!details.lastName)
      details.lastName = String(pick(values, ['LastName', 'lastName']) || '');

    // simple scalars
    if (!details.gender)
      details.gender = String(pick(values, ['Gender', 'gender']) || '');
    if (!details.maritalStatus)
      details.maritalStatus = String(
        pick(values, ['MaritalStatus', 'maritalStatus']) || ''
      );
    if (!details.licenseType)
      details.licenseType = String(
        pick(values, [
          'mainDriverLicense',
          'typeoflicensebind',
          'typeoflicense',
        ]) || ''
      );

    // birth date (prefer split; then string "29 Jun 1990")
    const bd = details.birthDate || { year: '', month: '', day: '' };
    if (!bd.year)
      bd.year = String(pick(values, ['DobYear', 'birthDate.year']) || '');
    if (!bd.month)
      bd.month = String(pick(values, ['DobMonth', 'birthDate.month']) || '');
    if (!bd.day)
      bd.day = String(pick(values, ['DobDay', 'birthDate.day']) || '');
    if (!bd.year || !bd.month || !bd.day) {
      const dobStr = pick(values, ['DateOfBirth', 'dateOfBirth']); // "29 Jun 1990"
      const parsed = parseDateDMonY(dobStr) || parseDateFlexible(dobStr);
      if (parsed) {
        if (!bd.year) bd.year = parsed.year;
        if (!bd.month) bd.month = parsed.month;
        if (!bd.day) bd.day = parsed.day;
      }
    }
    if (!details.birthDateString && bd.year && bd.month && bd.day) {
      details.birthDateString = `${bd.year}-${bd.month}-${bd.day}`;
    }
    details.birthDate = bd;

    // license first issue (object in form: licenseFirstIssue { year, month })
    const lfi = details.licenseFirstIssueDate || {
      year: '',
      month: '',
      day: '',
    };
    if (!lfi.year)
      lfi.year = String(
        pick(values, ['licenseFirstIssue.year', 'licenseissuedyear']) || ''
      );
    if (!lfi.month)
      lfi.month = String(
        pick(values, ['licenseFirstIssue.month', 'licenseissuedmonth']) || ''
      );
    // day may not exist in form values; leave blank
    details.licenseFirstIssueDate = lfi;

    // uninterrupted & lastClaim — prefer the SAME driver item if present
    if (details.uninterruptedCarInsurance === '' && sameDriverItem) {
      details.uninterruptedCarInsurance = toNum(
        sameDriverItem.ComprehensiveInsDuration
      );
    }
    if (details.lastClaimYears === '' && sameDriverItem) {
      details.lastClaimYears = toNum(sameDriverItem.LastClaimDuration);
    }

    // As a final fallback, allow form-level strings like "7+" / "6"
    if (details.uninterruptedCarInsurance === '') {
      details.uninterruptedCarInsurance = toNum(
        pick(values, ['uninterruptedCarInsurance'])
      );
    }
    if (details.lastClaimYears === '') {
      details.lastClaimYears = toNum(pick(values, ['lastClaimYears']));
    }

    return details;
  };

  const handleObjectSelection = ({
    selectedLabel,
    values,
    question,
    onChange,
    name,
    setError,
    setValidationState,
    setInputValue,
    nextFieldRef,
    onNext,
    validate, // optional
    writeToFormValues, // provided in your component
  }) => {
    // Always set the field itself
    onChange(name, selectedLabel);

    // Detect arrays explicitly from arrayPath; fall back to known roots
    const arrFromPath = getFromPath(values, question.arrayPath);
    const driversArr = Array.isArray(arrFromPath)
      ? arrFromPath
      : values?.Drivers;
    const vehiclesArr = Array.isArray(arrFromPath)
      ? arrFromPath
      : values?.Vehicles;

    const looksLikeDrivers = question.isReturningUserDriverSelector || false;

    const looksLikeVehicles = question.isReturningUserVehicleSelector || false;
    // ---------------- VEHICLE BRANCH ----------------
    if (looksLikeVehicles) {
      const list = Array.isArray(vehiclesArr) ? vehiclesArr : [];
      const vehicleItem =
        list.find((it) => eqi(it?.VehicleDescription, selectedLabel)) || null;
      // Only write carDetails if we have a matching item or at least a label
      const details = vehicleItem
        ? buildCarDetailsFromVehicle(vehicleItem, selectedLabel)
        : {
            VehicleYear: '',
            SearchDescription: formatSearchDescription(
              String(selectedLabel || '')
            ),
            SearchDescriptionRaw: String(selectedLabel || ''),
            VehicleKey: '',
          };

      writeToFormValues('carDetails', { ...details });

      setInputValue?.(selectedLabel);

      if (typeof validate === 'function') {
        if (validate(selectedLabel)) {
          setError('');
          setValidationState?.(name, true);
          nextFieldRef?.current?.focus() ?? onNext?.();
        } else {
          setError(question.validation?.message || 'Required');
          setValidationState?.(name, false);
        }
      } else {
        setError('');
        setValidationState?.(name, true);
        nextFieldRef?.current?.focus() ?? onNext?.();
      }
      return; // IMPORTANT: stop here (don’t run driver logic)
    }
    // ------------- END VEHICLE BRANCH ---------------

    if (!looksLikeDrivers) {
      // Not a driver picker: do NOT touch mainDriverDetails
      setInputValue?.(selectedLabel);
      if (typeof validate === 'function') {
        if (validate(selectedLabel)) {
          setError('');
          setValidationState?.(name, true);
          nextFieldRef?.current?.focus() ?? onNext?.();
        } else {
          setError(question.validation?.message || 'Required');
          setValidationState?.(name, false);
        }
      } else {
        setError('');
        setValidationState?.(name, true);
        nextFieldRef?.current?.focus() ?? onNext?.();
      }
      return;
    }
    // --------------------------------------------------------------------------

    // Find driver item by FullName (from either detected list or values.Drivers)
    const list = Array.isArray(driversArr) ? driversArr : [];
    const driverItem =
      list.find((it) => eqi(it?.FullName, selectedLabel)) ||
      (Array.isArray(values?.Drivers)
        ? values.Drivers.find((it) => eqi(it?.FullName, selectedLabel))
        : null) ||
      null;

    // If we still didn't find a driver item AND this isn't the main fullName, bail
    const isMain = eqi(selectedLabel, values?.fullName);
    writeToFormValues('mainDriver', isMain ? 'Yes' : 'No');
    if (!driverItem && !isMain) {
      // Nothing to build; don't write mainDriverDetails
      setInputValue?.(selectedLabel);
      if (typeof validate === 'function') {
        if (validate(selectedLabel)) {
          setError('');
          setValidationState?.(name, true);
          nextFieldRef?.current?.focus() ?? onNext?.();
        } else {
          setError(question.validation?.message || 'Required');
          setValidationState?.(name, false);
        }
      } else {
        setError('');
        setValidationState?.(name, true);
        nextFieldRef?.current?.focus() ?? onNext?.();
      }
      return;
    }

    // Build from driver item (or minimal shell if we only know it's the main)
    let details = driverItem
      ? buildFromDriverItem(driverItem, selectedLabel)
      : {
          fullName: String(selectedLabel || ''),
          lastName: '',
          firstName: '',
          birthDate: { year: '', month: '', day: '' },
          birthDateString: '',
          gender: '',
          maritalStatus: '',
          licenseFirstIssueDate: { year: '', month: '', day: '' },
          licenseType: '',
          uninterruptedCarInsurance: '',
          lastClaimYears: '',
        };

    // If it’s the same as the form’s main fullName, supplement blanks from formValues
    if (isMain) {
      details = supplementFromFormValues(details, values, driverItem);
    }

    if (
      question.name === 'mainDriverDetails.lastClaimYears' ||
      question.name === 'mainDriverDetails.uninterruptedCarInsurance'
    ) {
      // normalize both fields so 0 → "Never", 7+ → "7+" etc.
      details.uninterruptedCarInsurance = yearsToLabel(
        details.uninterruptedCarInsurance
      );
      details.lastClaimYears = yearsToLabel(details.lastClaimYears);

      // also keep the "blank → Never" safeguard
      if (!details.uninterruptedCarInsurance)
        details.uninterruptedCarInsurance = 'Never';
      if (!details.lastClaimYears) details.lastClaimYears = 'Never';
    }

    // Persist ONLY mainDriverDetails
    writeToFormValues('mainDriverDetails', details);

    // Keep Autocomplete input in sync and advance/validate
    setInputValue?.(selectedLabel);

    if (typeof validate === 'function') {
      if (validate(selectedLabel)) {
        setError('');
        setValidationState?.(name, true);
        nextFieldRef?.current?.focus() ?? onNext?.();
      } else {
        setError(question.validation?.message || 'Required');
        setValidationState?.(name, false);
      }
    } else {
      setError('');
      setValidationState?.(name, true);
      nextFieldRef?.current?.focus() ?? onNext?.();
    }
  };

  const applySyncMap = (selectedLabel) => {
    const syncMap = question?.syncMap;
    if (!syncMap || !syncMap.length) return;

    const listSrc = getFromPath(values, question.arrayPath);
    const list = Array.isArray(listSrc) ? listSrc : [];
    const matchField = question.matchBy || question.labelField || 'label';

    // Try to find the chosen object (only if arrayPath provided)
    const item = Array.isArray(list)
      ? list.find(
          (it) =>
            String(it?.[matchField] ?? '')
              .trim()
              .toLowerCase() === String(selectedLabel).trim().toLowerCase()
        )
      : null;

    for (const m of syncMap) {
      if (!m || !m.to) continue;

      if (m.clear) {
        writeToFormValues(m.to, '');
        continue;
      }

      // Explicit literal
      if ('value' in m) {
        const val = m.value === '__SELECTED__' ? selectedLabel : m.value;
        writeToFormValues(m.to, coerceVal(val, m.coerce));
        continue;
      }

      // Copy from item when available
      if (m.from && item) {
        const raw = item[m.from];
        writeToFormValues(m.to, coerceVal(raw, m.coerce));
        continue;
      }

      // Fallback: write the selection itself
      writeToFormValues(m.to, coerceVal(selectedLabel, m.coerce));
    }
  };

  const builtOptions = useMemo(() => {
    if (question.yearRange) {
      const currentYear = new Date().getFullYear();
      const maxYear =
        question.maxYearsAgo != null
          ? currentYear - question.maxYearsAgo
          : currentYear;
      const minYear =
        question.minYearsAgo != null
          ? currentYear - question.minYearsAgo
          : question.minYear || 1995;
      const years = [];
      for (let y = maxYear; y >= minYear; y--) {
        years.push(String(y));
      }
      return years;
    }

    const {
      arrayPath,
      labelField = 'VehicleDescription',
      valueBuilder,
      sort = true,
    } = question || {};
    if (!arrayPath) return question.options ?? [];

    const arr = Array.isArray(getFromPath(values, arrayPath))
      ? getFromPath(values, arrayPath)
      : [];

    const labelOf = (item) => {
      if (!item) return '';
      if (valueBuilder && typeof valueBuilder === 'string') {
        return valueBuilder
          .replace(/\$\{([^}]+)\}/g, (_, k) => item[k] ?? '')
          .replace(/\s+/g, ' ')
          .trim();
      }
      const raw =
        item[labelField] ??
        (item.VehicleMake || item.VehicleModel
          ? `${item.VehicleMake || ''} ${item.VehicleModel || ''}`
          : '');
      return String(raw || '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const labels = uniqueStrings(arr.map(labelOf).filter(Boolean));
    if (sort)
      labels.sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
    return labels;
  }, [
    values,
    question?.arrayPath,
    question?.labelField,
    question?.valueBuilder,
    question?.sort,
    question?.options,
  ]);

  const options = useMemo(() => {
    const base = (
      builtOptions.length ? builtOptions : (question.options ?? [])
    ).slice();
    if (question.extraOptionLabel) base.push(question.extraOptionLabel);
    return base;
  }, [builtOptions, question.options, question.extraOptionLabel]);

  const [inputValue, setInputValue] = useState(
    typeof value === 'string' ? value : ''
  );

  useEffect(() => {
    if (isSearchEnabled) {
      setInputValue(typeof value === 'string' ? value : '');
    }
  }, [value, isSearchEnabled]);

  useEffect(() => {
    registerValidation?.(name, validate);
  }, [value, inputValue, isSearchEnabled, options]);

  // If we land on this question with the extra-option label lingering as the
  // stored value (e.g. after "+ Add Another Vehicle" then navigating back),
  // clear it so the field shows the placeholder instead of the label text.
  // Mount-only: must not re-run when the user selects the label in-session,
  // otherwise it would fight the auto-advance-on-select behaviour.
  useEffect(() => {
    if (question.extraOptionLabel && value === question.extraOptionLabel) {
      onChange(name, '');
      if (isSearchEnabled) setInputValue('');
      setError('');
      setValidationState?.(name, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!value) return;

    // detect driver list (explicit arrayPath or fallback to values.Drivers)
    const arrSrc = getFromPath(values, question.arrayPath);
    const arr = Array.isArray(arrSrc) ? arrSrc : values?.Drivers;
    const looksLikeDrivers =
      Array.isArray(arr) &&
      arr.length > 0 &&
      typeof arr[0] === 'object' &&
      ('FullName' in arr[0] || 'Name' in arr[0] || 'Surname' in arr[0]);

    if (!looksLikeDrivers) return;

    const isMain = eqi(value, values?.fullName);
    const shouldBe = isMain ? 'Yes' : 'No';
    if (values?.mainDriver !== shouldBe) {
      writeToFormValues('mainDriver', shouldBe);
    }
    const driverItem =
      (arr || []).find((it) => eqi(it?.FullName, value)) || null;
    if (driverItem && !values?.mainDriverDetails) {
      const details = buildFromDriverItem(driverItem, value);
      writeToFormValues('mainDriverDetails', details);
    }
  }, [value, values?.fullName, values?.mainDriver, question?.arrayPath]);

  useEffect(() => {
    if (!value) return;

    const arrSrc = getFromPath(values, question.arrayPath);
    const vehiclesArr = Array.isArray(arrSrc) ? arrSrc : values?.Vehicles;
    const looksLikeVehicles =
      Array.isArray(vehiclesArr) &&
      vehiclesArr.length > 0 &&
      typeof vehiclesArr[0] === 'object' &&
      'VehicleDescription' in vehiclesArr[0];

    if (!looksLikeVehicles) return;

    const vehicleItem =
      (vehiclesArr || []).find((it) => eqi(it?.VehicleDescription, value)) ||
      null;

    if (vehicleItem && !values?.carDetails) {
      const details = buildCarDetailsFromVehicle(vehicleItem, value);
      writeToFormValues('carDetails', details);
    }
  }, [value, question?.arrayPath]);

  // ---------- validation ----------
  const findCanonicalOption = (txt) => {
    if (!txt) return null;
    const idx = options.findIndex(
      (o) => String(o).toLowerCase() === String(txt).toLowerCase()
    );
    return idx >= 0 ? options[idx] : null;
  };

  const validate = (input = value) => {
    const requiredInvalid =
      question.validation?.required &&
      (isSearchEnabled ? !inputValue?.trim() : !input);

    if (requiredInvalid) {
      setError(question.validation?.message || 'Required');
      setValidationState?.(name, false);
      return false;
    }

    if (isSearchEnabled) {
      const match = findCanonicalOption(inputValue);
      if (!match) {
        setError(question.validation?.message || 'Required');
        setValidationState?.(name, false);
        return false;
      }
    }

    setError('');
    setValidationState?.(name, true);
    return true;
  };

  const goNextIfValid = (overrideValue) => {
    if (validate(overrideValue)) {
      nextFieldRef?.current?.focus() ?? onNext?.();
    }
  };

  // ---------- tooltip border overrides ----------
  const borderOverrides = question.tooltip?.message
    ? {
        '&:hover fieldset': { borderColor: '#E3E800' },
        '&.Mui-focused fieldset': { borderColor: '#E3E800' },
      }
    : {};

  // ---------- Select (non-search) ----------
  const handleChange = (e) => {
    const selected = e.target.value;

    if (question.extraOptionLabel && selected === question.extraOptionLabel) {
      // Clear configured fields when the special extra option is chosen
      clearPaths(question.clearFieldsOnExtraOption, writeToFormValues, '');

      // (Optional) also clear this dropdown's saved value so it shows placeholder again
      onChange(name, question.extraOptionLabel);

      // Keep any existing "open modal / add row" action
      question.onExtraOption?.();

      // Consider validation as satisfied for this action, or mark neutral:
      setError('');
      setValidationState?.(name, true);

      // Advance to the next question just like pressing the primary button.
      // Defer so the onChange above is committed before validation re-runs.
      if (onExtraOptionAdvance) {
        setTimeout(() => onExtraOptionAdvance(), 0);
      }

      return; // stop normal syncing logic
    }

    // Clear configured fields whenever this field's value actually changes
    if (question.clearFieldsOnChange && selected !== value) {
      clearPaths(question.clearFieldsOnChange, writeToFormValues, '');
    }

    // Branch: main vs additional driver
    handleObjectSelection({
      selectedLabel: selected,
      values,
      question,
      onChange,
      name,
      setError,
      setValidationState,
      setInputValue, // harmless for Select
      nextFieldRef,
      onNext,
      validate,
      writeToFormValues,
    });
    applySyncMap(selected);
  };

  const handleKeyDownSelect = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      goNextIfValid();
    }
  };

  const select = (
    <Select
      labelId={labelId}
      value={value ?? ''}
      onChange={readOnly ? undefined : handleChange}
      displayEmpty
      inputRef={fieldRef}
      onKeyDown={readOnly ? undefined : handleKeyDownSelect}
      variant="outlined"
      inputProps={{ readOnly }}
      IconComponent={readOnly ? () => null : undefined}
      // Label shows only when there IS a value
      label={value ? toTitleCase(question.label) : undefined}
      sx={{
        height: fieldHeight,
        transition: 'none',
        cursor: readOnly ? 'default' : undefined,
        '& .MuiSelect-select': {
          cursor: readOnly ? 'default' : undefined,
          fontFamily: 'Inter, sans-serif',
          fontSize,
          lineHeight: '1.2rem',
          letterSpacing: '-0.02em',
          color: value ? '#162F3B' : '#738289',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          transition: 'none',
          textTransform: 'none',
        },
        '& .MuiSelect-icon': {
          color: '#162F3B',
          fontSize: 24,
        },
      }}
      MenuProps={{
        disablePortal: false,
        slots: { paper: ScrollFadePaper },
        slotProps: {
          paper: {
            sx: {
              mt: '10px',
              width: menuWidth,
              minWidth: menuWidth,
              maxHeight: `calc(${fieldHeight} * 6)`,
              overflowY: 'auto',
              border: '1px solid #A2ACB1',
              borderRadius: '12px',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
              '&::-webkit-scrollbar': { width: 0, height: 0 },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              zIndex: 1301,
            },
          },
        },
        MenuListProps: {
          sx: {
            '&::-webkit-scrollbar': { width: 0, height: 0 },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            maxHeight: `calc(${fieldHeight} * 6)`,
            overflowY: 'auto',
          },
        },
      }}
      renderValue={(selected) =>
        selected ? (
          <Typography
            sx={{
              color: '#162F3B',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize,
              lineHeight: '1.2rem',
            }}
          >
            {selected}
          </Typography>
        ) : (
          <Typography
            sx={{
              color: '#738289',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize,
              lineHeight: '1.2rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {question.placeholder}
          </Typography>
        )
      }
    >
      {options.map((option, idx) => (
        <MenuItem
          key={idx}
          value={option}
          onClick={() => {
            // If user re-selects the same item, we still want to advance
            if (option === value) {
              goNextIfValid(option);
            }
          }}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize,
            color: '#162F3B',
            height: fieldHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            px: '28px',
            '&.Mui-selected': { backgroundColor: 'transparent !important' },
          }}
        >
          {option}
        </MenuItem>
      ))}
    </Select>
  );

  // ---------- Autocomplete (search-enabled) ----------
  const StyledPopper = (props) => (
    <Popper {...props} placement="bottom-start" />
  );
  const StyledPaper = (props) => (
    <ScrollFadePaper
      {...props}
      sx={{
        mt: '10px',
        width: menuWidth,
        minWidth: menuWidth,
        maxHeight: `calc(${fieldHeight} * 6)`,
        overflowY: 'auto',
        border: '1px solid #A2ACB1',
        borderRadius: '12px',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
        '&::-webkit-scrollbar': { width: 0, height: 0 },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        zIndex: 1301,
        '& .MuiAutocomplete-listbox': {
          maxHeight: `calc(${fieldHeight} * 6)`,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 0, height: 0 },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        },
      }}
    />
  );

  const handleAutoChange = (_evt, newValue) => {
    if (question.extraOptionLabel && newValue === question.extraOptionLabel) {
      // Clear configured fields when the special extra option is chosen
      clearPaths(question.clearFieldsOnExtraOption, writeToFormValues, '');

      // (Optional) clear the field value + input text; leave placeholder visible
      onChange(name, question.extraOptionLabel);
      setInputValue(question.extraOptionLabel);

      // Trigger any side-effect (open modal, etc.)
      question.onExtraOption?.();

      setError('');
      setValidationState?.(name, true);

      // Advance to the next question just like pressing the primary button.
      if (onExtraOptionAdvance) {
        setTimeout(() => onExtraOptionAdvance(), 0);
      }
      return;
    }
    if (typeof newValue === 'string') {
      const canonical = findCanonicalOption(newValue);
      if (canonical) {
        // Clear configured fields whenever this field's value actually changes
        if (question.clearFieldsOnChange && canonical !== value) {
          clearPaths(question.clearFieldsOnChange, writeToFormValues, '');
        }

        handleObjectSelection({
          selectedLabel: canonical,
          values,
          question,
          onChange,
          name,
          setError,
          setValidationState,
          setInputValue,
          nextFieldRef,
          onNext,
          validate,
          writeToFormValues,
        });

        applySyncMap(canonical);
      } else {
        // Keep your existing invalid-state behavior
        onChange?.(name, newValue);
        setError(question.validation?.message || 'Required');
        setValidationState?.(name, false);
      }
    }
  };

  const handleAutoInputChange = (_evt, newInputValue) => {
    // If numericMode, keep only digits (helps desktop users)
    const cleaned = numericMode
      ? newInputValue.replace(/\D+/g, '')
      : newInputValue;
    setInputValue(cleaned);
    if (!cleaned && question.validation?.required) {
      setError(question.validation?.message || 'Required');
    } else {
      setError('');
    }
  };

  const handleAutoBlur = () => {
    if (!inputValue?.trim() && question.validation?.required) {
      setError(question.validation?.message || 'Required');
      setValidationState?.(name, false);
      return;
    }
    const canonical = findCanonicalOption(inputValue);
    if (!canonical && inputValue) {
      setError(question.validation?.message || 'Required');
      setValidationState?.(name, false);
    } else {
      setError('');
      setValidationState?.(name, true);
    }
  };

  // Label shows when value exists; placeholder otherwise
  const placeholderText = inputValue ? '' : question.placeholder || '';

  const auto = (
    <Autocomplete
      freeSolo
      disablePortal={false}
      disableClearable
      readOnly={readOnly}
      options={options}
      value={value ?? null}
      inputValue={inputValue}
      onChange={readOnly ? undefined : handleAutoChange}
      onInputChange={readOnly ? undefined : handleAutoInputChange}
      onBlur={readOnly ? undefined : handleAutoBlur}
      PopperComponent={StyledPopper}
      PaperComponent={StyledPaper}
      filterOptions={(opts, state) => {
        const q = state.inputValue?.toLowerCase() ?? '';
        if (!q) return opts;
        return opts.filter((o) => String(o).toLowerCase().includes(q));
      }}
      ListboxProps={{
        sx: {
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiAutocomplete-option.Mui-focused[aria-selected="true"]': {
            backgroundColor: 'transparent !important',
          },
        },
      }}
      getOptionLabel={(opt) =>
        typeof opt === 'string' ? toTitleCase(opt) : toTitleCase(String(opt))
      }
      renderOption={(props, option) => (
        <li {...props} style={{ listStyle: 'none' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize,
              color: '#162F3B',
              height: fieldHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              px: '28px',
              width: '100%',
            }}
          >
            {toTitleCase(option)}
          </Typography>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={fieldRef}
          label={inputValue ? toTitleCase(question.label) : ''}
          placeholder={placeholderText}
          variant="outlined"
          type="text" // keep text; rely on inputMode for keyboards
          inputProps={{
            ...params.inputProps,
            inputMode: numericMode ? 'numeric' : 'text',
            pattern: numericMode ? '[0-9]*' : undefined,
          }}
          onKeyDown={(e) => {
            // Block non-digits when numericMode (while still allowing control keys)
            if (numericMode) {
              const allowed =
                [
                  'Backspace',
                  'Delete',
                  'ArrowLeft',
                  'ArrowRight',
                  'Tab',
                  'Home',
                  'End',
                ].includes(e.key) || /^[0-9]$/.test(e.key);
              if (!allowed) e.preventDefault();
            }
            if (e.key === 'Enter') e.preventDefault();
          }}
          InputLabelProps={{
            shrink: !!inputValue, // label only when value exists
            sx: { color: '#738289' },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: fieldHeight,
              borderRadius: '12px',
              paddingTop: '16px',
              paddingRight: '12px',
              paddingBottom: '16px',
              paddingLeft: '12px',
              gap: '8px',
              borderWidth: '1px',
              transition: 'none',
              '& fieldset': {
                borderRadius: '12px',
                borderWidth: '1px',
                borderColor: '#A2ACB1',
              },
              ...borderOverrides,
              '& input': {
                fontFamily: 'Inter, sans-serif',
                fontSize,
                lineHeight: '1.2rem',
                letterSpacing: '-0.02em',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                color: inputValue ? '#162F3B' : '#738289',
                '&::placeholder': {
                  color: '#738289',
                  opacity: 1,
                },
              },
              '& .MuiAutocomplete-endAdornment': {
                right: 8,
                '& .MuiAutocomplete-clearIndicator': { display: 'none' },
                '& .MuiAutocomplete-popupIndicator': { color: '#162F3B' },
              },
            },
          }}
        />
      )}
      componentsProps={{
        paper: { elevation: 0 },
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
        },
      }}
      sx={{
        '& .MuiAutocomplete-option[aria-selected="true"]': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiAutocomplete-option.Mui-focused': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiAutocomplete-option.Mui-focused[aria-selected="true"]': {
          backgroundColor: 'transparent !important',
        },
      }}
    />
  );

  const wrappedControl = (content) => (
    <FormControl
      fullWidth
      error={!!error}
      variant="outlined"
      sx={{
        width: responsiveWidth,
        mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        maxWidth: '100%',
        transition: 'none',
        '& .MuiOutlinedInput-root': {
          borderRadius: '12px',
          paddingTop: '16px',
          paddingRight: '12px',
          paddingBottom: '16px',
          paddingLeft: '12px',
          gap: '8px',
          borderWidth: '1px',
          transition: 'none',
          '& fieldset': {
            borderRadius: '12px',
            borderWidth: '1px',
            borderColor: '#A2ACB1',
          },
          ...borderOverrides,
        },
        '& .MuiFormHelperText-root': {
          textAlign: 'right',
          fontFamily: 'Inter, sans-serif',
          fontSize: isMobile ? '0.6rem' : '0.75rem',
          color: '#D32F2F',
          mt: '4px',
          pr: '8px',
        },
      }}
    >
      {/* SELECT mode: show label only when there IS a value */}
      {!isSearchEnabled && value && (
        <InputLabel
          id={labelId}
          shrink
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            letterSpacing: '-0.02em',
            color: '#738289',
          }}
        >
          {question.label}
        </InputLabel>
      )}

      {question.tooltip?.message ? (
        <Tooltip
          title={<span>{question.tooltip.message}</span>}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                letterSpacing: '-0.002em',
                borderRadius: '12px',
                border: '2px solid #E3E800',
                backgroundColor: '#FFFFFF',
                padding: '16px',
                boxShadow: '0px 3px 8px #162F3B1F, 0px 1px 2px #A2ACB133',
                maxWidth: '297px',
                color: '#000',
                '& .MuiTooltip-arrow': { color: '#E3E800' },
              },
            },
            arrow: { sx: { color: '#E3E800' } },
          }}
        >
          {content}
        </Tooltip>
      ) : (
        content
      )}
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );

  return wrappedControl(isSearchEnabled ? auto : select);
};

export default DropdownComponent;
